// ============================================
// 📄 GERADOR DE PDF - PRESCRIÇÕES E DOCUMENTOS
// ============================================
// Gera PDF em nova janela para impressão
// Layout baseado no padrão MEMED do ambulatório

import { Paciente } from "../../../types";
import logoBase64 from "./logoBase64";
import { calcularIdade } from "../../../utils/dates";
// 🔒 B317 (AI_143, achado A1 da auditoria externa) — TODO valor de DADO
// interpolado no HTML (paciente, config do médico, medicamento, observação,
// título) passa por escapeHtml. Fragmentos HTML legítimos (selo, QR, logo,
// anexos SUS, saída do processarMarkdown — que já escapa na entrada) NÃO
// são re-escapados. Regra: ver src/shared/utils/escapeHtml.ts.
import { escapeHtml } from "../../../shared/utils/escapeHtml";

interface ConfigPDF {
  nomeAmbulatorio: string;
  nomeMedico: string;
  crm: string;
  endereco: string;
  cidade?: string;
  telefone?: string;
}

interface ExamePDF {
  nome: string;
  tuss: string;
  categoria?: "laboratorial" | "imagem" | "outro";
  observacao?: string;
}

// ============================================
// GERAR PDF DE PEDIDO DE EXAMES
// ============================================
// B289 (AI_127): monta o HTML do pedido de exames e o RETORNA (sem imprimir).
// Fonte única usada pela impressão (gerarPDFPedidoExames, abaixo) E pela
// assinatura digital — que precisa do mesmo HTML para virar PDF binário no
// /api/gerar-pdf. Assim o que se assina é byte a byte o que se imprime.
export function montarPedidoExamesHTML(
  paciente: Paciente,
  exames: ExamePDF[],
  config: ConfigPDF,
  observacao?: string,
  // Anexo opcional (ex.: ficha GAL) impresso no MESMO documento, em folha
  // própria após o pedido. Cada anexo traz seus estilos e corpo já prontos.
  anexos?: { estilos: string; corpo: string }[],
  // B289 (AI_127) — selo ICP-Brasil (só no PDF assinado), no rodapé do pedido.
  selo?: string
): string {
  const laboratoriais = exames.filter((e) => e.categoria !== "imagem");
  const imagem = exames.filter((e) => e.categoria === "imagem");

  const dataAtual = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  // Cabeçalho + identificação do paciente, repetidos em TODA página (thead).
  const headerHTML = `<div class="header">
        <div class="header-logo"><img src="${logoBase64}" alt="Logo" /></div>
        <div class="header-text">
          <h1>${escapeHtml(config.nomeAmbulatorio)}</h1>
          <p>${escapeHtml(config.nomeMedico)}${
    config.crm ? ` — ${escapeHtml(config.crm)}` : ""
  }</p>
        </div>
      </div>
      <div class="paciente-cab">
        <p><strong>Nome:</strong> ${escapeHtml(paciente.nome)}</p>
        ${(() => {
          // B307 (AI_138) — pedido da enfermeira: CPF, CNS e HOSPUB do paciente na
          // MESMA linha (só os preenchidos), para não acrescentar altura ao thead
          // (a paginação assinada — B298 — depende da altura do cabeçalho). Escopo:
          // só o Pedido de Exames (cabeçalho inline desta função).
          const ids = [
            paciente.cpf
              ? `<strong>CPF:</strong> ${escapeHtml(paciente.cpf)}`
              : "",
            paciente.cns
              ? `<strong>CNS:</strong> ${escapeHtml(paciente.cns)}`
              : "",
            paciente.hospub
              ? `<strong>HOSPUB:</strong> ${escapeHtml(paciente.hospub)}`
              : "",
          ].filter(Boolean);
          return ids.length ? `<p>${ids.join("&nbsp;·&nbsp;")}</p>` : "";
        })()}
      </div>`;

  // B289 (AI_128) — cabeçalho + paciente no thead e o rodapé com o SELO no tfoot,
  // AMBOS repetem em TODA página (decisão de Leonardo: carimbo em todas, nunca órfão).
  // B298 (AI_136) — QUANDO ASSINADO, o rodapé NÃO repete nome/CRM/endereço: eles já
  // estão no CABEÇALHO (thead) e DENTRO do selo. Isso ENCURTA o carimbo o bastante
  // para o Chromium REPETIR o tfoot em toda página E RESERVAR seu espaço — um tfoot
  // alto demais o motor NÃO repete (some das páginas do meio) e transborda órfão.
  // Sem assinatura, mantém o rodapé completo (curto → sem risco de órfão; e serve
  // de linha para assinatura manual).
  const rodapeSecao = selo
    ? `<div class="rodape">
          <div class="rodape-data" style="text-align: right; font-size: 13px; color: #333;">${
            config.cidade ? escapeHtml(config.cidade) + ", " : ""
          }${dataAtual}</div>
          <div style="margin-top: 14px; text-align: center;">${selo}</div>
        </div>`
    : `<div class="rodape">
          <div class="rodape-crm">${escapeHtml(config.nomeMedico)}${
        config.crm ? ` — ${escapeHtml(config.crm)}` : ""
      }</div>
          ${
            config.endereco
              ? `<div class="rodape-endereco">${escapeHtml(
                  config.endereco
                )}</div>`
              : ""
          }
          <div class="rodape-data" style="text-align: right; margin-top: 12px; font-size: 13px; color: #333;">${
            config.cidade ? escapeHtml(config.cidade) + ", " : ""
          }${dataAtual}</div>
        </div>`;

  // B298 (AI_136): UMA tabela só para o pedido inteiro (laboratório + cada exame
  // de imagem). Antes eram tabelas SEPARADAS — e o table-footer-group só reserva
  // o espaço do carimbo (alto: selo+QR) quando a tabela é a ÚNICA no fluxo; com
  // várias tabelas o carimbo da 1ª ficava órfão numa página sem exames. Numa
  // tabela única, o tfoot reserva por página e repete em TODAS, nunca sozinho
  // (regra do Leonardo). Cada exame é uma linha (<tr> via gerarConteudoExames);
  // cada seção nova (imagem) começa em página nova pela classe `sec-nova`
  // (break-before) na linha de título.
  const tituloRow = (novaSecao: boolean) =>
    `<tr><td class="${
      novaSecao ? "sec-nova" : ""
    }"><div class="tipo-documento">Pedido de Exame</div></td></tr>`;

  let linhasTbody = "";
  let temSecao = false;

  // Laboratoriais: todos juntos = um pedido para o laboratório.
  if (laboratoriais.length > 0) {
    linhasTbody += tituloRow(false) + gerarConteudoExames(laboratoriais, observacao);
    temSecao = true;
  }

  // Uma SEÇÃO para CADA exame de imagem (cada um vai a um setor diferente) —
  // começa em página nova (break-before) exceto se for a primeira seção do doc.
  imagem.forEach((exame) => {
    linhasTbody += tituloRow(temSecao) + gerarConteudoExames([exame], exame.observacao);
    temSecao = true;
  });

  // Fallback (improvável): nenhum classificado.
  if (!temSecao) {
    linhasTbody = tituloRow(false) + gerarConteudoExames(exames, observacao);
  }

  const tabela = `
    <table class="doc-layout">
      <thead><tr><td>${headerHTML}</td></tr></thead>
      <tfoot><tr><td>${rodapeSecao}</td></tr></tfoot>
      <tbody>${linhasTbody}</tbody>
    </table>`;

  // Anexos (ficha GAL etc.) entram após o pedido, cada um em folha própria —
  // FORA da tabela (mantêm o layout SUS oficial, sem o rodapé corrido do app).
  const estilosAnexos = (anexos || []).map((a) => a.estilos).join("\n");
  const corpoAnexos = (anexos || []).map((a) => a.corpo).join("");

  return montarHTMLMultiPagina(
    config,
    tabela,
    `Pedido de Exame - ${paciente.nome}`,
    estilosAnexos,
    { corpoAnexos }
  );
}

// B289 (AI_127): impressão do pedido de exames — chama a fonte única acima.
export function gerarPDFPedidoExames(
  paciente: Paciente,
  exames: ExamePDF[],
  config: ConfigPDF,
  observacao?: string,
  anexos?: { estilos: string; corpo: string }[]
) {
  abrirPDFEmNovaJanela(
    montarPedidoExamesHTML(paciente, exames, config, observacao, anexos)
  );
}

// ============================================
// GERAR PDF DE TEXTO LIVRE
// ============================================
// ============================================
// PROCESSAR MARKDOWN → HTML
// ============================================
function processarMarkdown(texto: string): string {
  const linhas = texto.split("\n");
  let html = "";
  let dentroLista = false;

  for (let i = 0; i < linhas.length; i++) {
    let linha = linhas[i];

    // 🔒 B317 (AI_143): escapar ANTES do mini-markdown — o texto livre (que
    // pode vir de PDF importado ou da IA) nunca entra cru no HTML. Escapar
    // primeiro não afeta os marcadores **/*/#/- (nenhum contém & < > " ').
    linha = escapeHtml(linha);

    // Aplicar inline: **negrito** e *itálico*
    linha = linha.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    linha = linha.replace(/\*(.+?)\*/g, "<em>$1</em>");

    // Títulos: ### H3
    if (linha.startsWith("### ")) {
      if (dentroLista) {
        html += "</ul>";
        dentroLista = false;
      }
      html += `<h3 style="font-size:13px; font-weight:700; margin:16px 0 4px 0; color:#1a1a1a; text-transform:uppercase; letter-spacing:0.5px;">${linha.substring(
        4
      )}</h3>`;
      continue;
    }

    // Títulos: ## H2
    if (linha.startsWith("## ")) {
      if (dentroLista) {
        html += "</ul>";
        dentroLista = false;
      }
      html += `<h2 style="font-size:14px; font-weight:700; margin:20px 0 6px 0; color:#1a1a1a; text-transform:uppercase; letter-spacing:0.5px;">${linha.substring(
        3
      )}</h2>`;
      continue;
    }

    // Títulos: # H1
    if (linha.startsWith("# ")) {
      if (dentroLista) {
        html += "</ul>";
        dentroLista = false;
      }
      html += `<h1 style="font-size:15px; font-weight:700; margin:20px 0 8px 0; color:#1a1a1a; text-transform:uppercase;">${linha.substring(
        2
      )}</h1>`;
      continue;
    }

    // Separador ---
    if (linha.trim() === "---") {
      if (dentroLista) {
        html += "</ul>";
        dentroLista = false;
      }
      html += `<hr style="border:none; border-top:1px solid #ddd; margin:16px 0;" />`;
      continue;
    }

    // Item de lista: - texto
    if (linha.match(/^[-•]\s/)) {
      if (!dentroLista) {
        html += `<ul style="margin:6px 0 6px 20px; padding:0;">`;
        dentroLista = true;
      }
      html += `<li style="font-size:13px; line-height:1.7; margin-bottom:3px;">${linha.substring(
        2
      )}</li>`;
      continue;
    }

    // Item de lista numerada: 1. texto
    if (linha.match(/^\d+\.\s/)) {
      if (dentroLista) {
        html += "</ul>";
        dentroLista = false;
      }
      const texto = linha.replace(/^\d+\.\s/, "");
      html += `<p style="font-size:13px; line-height:1.7; margin:4px 0; padding-left:4px;">${
        linha.match(/^\d+/)?.[0]
      }. ${texto}</p>`;
      continue;
    }

    // Fechar lista se linha não é item
    if (dentroLista) {
      html += "</ul>";
      dentroLista = false;
    }

    // Linha vazia → espaço entre parágrafos
    if (linha.trim() === "") {
      html += `<div style="height:8px;"></div>`;
      continue;
    }

    // Parágrafo normal
    html += `<p style="font-size:13px; line-height:1.8; margin:4px 0;">${linha}</p>`;
  }

  if (dentroLista) html += "</ul>";
  return html;
}

// B289 (AI_127): monta o HTML do Texto Livre e o RETORNA (sem imprimir). Fonte
// única usada pela impressão (gerarPDFTextoLivre, abaixo) E pela assinatura
// digital. O selo ICP-Brasil entra no rodapé (só no PDF assinado) — o montarHTML
// já suporta opcoes.seloAssinatura.
export function montarTextoLivreHTML(
  paciente: Paciente,
  titulo: string,
  conteudo: string,
  config: ConfigPDF,
  opcoes?: { rodapeSimplificado?: boolean },
  selo?: string
): string {
  const conteudoHTML = processarMarkdown(conteudo);
  return montarHTML(
    config,
    paciente,
    titulo,
    `<div style="margin-top: 24px;">${conteudoHTML}</div>`,
    undefined,
    { ...opcoes, seloAssinatura: selo }
  );
}

export function gerarPDFTextoLivre(
  paciente: Paciente,
  titulo: string,
  conteudo: string,
  config: ConfigPDF,
  // B250 (AI_113) — opções do documento. `rodapeSimplificado`: rodapé com APENAS
  // "Cidade, Data" (sem nome/CRM/endereço), p/ o Laudo Especial, cujo corpo já
  // traz o bloco de assinatura (Médico responsável / CRM). Default = comportamento
  // atual (Texto Livre e demais documentos inalterados).
  opcoes?: { rodapeSimplificado?: boolean }
) {
  abrirPDFEmNovaJanela(
    montarTextoLivreHTML(paciente, titulo, conteudo, config, opcoes)
  );
}

// ============================================
// GERAR PDF DE RECEITUÁRIO
// ============================================
// B212 F4 (AI_126): monta o HTML do receituário e o RETORNA (sem imprimir).
// Fonte única do HTML — usada pela impressão (gerarPDFReceituario, abaixo) E
// pela assinatura digital (que precisa do mesmo HTML para virar PDF binário no
// /api/gerar-pdf). Assim o que se assina é byte a byte o que se imprime.
export function montarReceituarioHTML(
  paciente: Paciente,
  medicamentos: { nome: string; posologia: string }[],
  config: ConfigPDF,
  // Anexo opcional (ex.: LME) impresso no MESMO documento, em folha própria
  // após a receita. Cada anexo traz seus estilos e corpo já prontos.
  anexos?: { estilos: string; corpo: string }[],
  // B212 F4 (AI_126) — selo ICP-Brasil no rodapé (só no PDF assinado).
  selo?: string
): string {
  return montarHTML(
    config,
    paciente,
    "Receituário",
    gerarConteudoReceituario(medicamentos),
    anexos,
    selo ? { seloAssinatura: selo } : undefined
  );
}

export function gerarPDFReceituario(
  paciente: Paciente,
  medicamentos: { nome: string; posologia: string }[],
  config: ConfigPDF,
  anexos?: { estilos: string; corpo: string }[]
) {
  abrirPDFEmNovaJanela(montarReceituarioHTML(paciente, medicamentos, config, anexos));
}

// ============================================
// GERAR PDF DE RECEITUÁRIO COM CONTROLE ESPECIAL
// ============================================
// B212 F4 (AI_126): monta o HTML do receituário com controle especial (receita
// simples + 2 vias de controle + anexos) e o RETORNA. Fonte única usada pela
// impressão (gerarPDFReceituarioComControle, abaixo) E pela assinatura digital.
export function montarReceituarioComControleHTML(
  paciente: Paciente,
  medicamentos: { nome: string; posologia: string }[],
  config: ConfigPDF,
  // Anexo opcional (ex.: LME) impresso no MESMO documento, após as vias.
  anexos?: { estilos: string; corpo: string }[],
  // B212 F4 (AI_126) — selo ICP-Brasil na página da receita (só no PDF assinado).
  selo?: string,
  // B289 (AI_128) — selo COMPACTO (estilo LME) no campo ASSINATURA das vias de
  // controle (só no PDF assinado). Pedido de Leonardo: encaixar no campo próprio.
  seloCompacto?: string
): string {
  const dataAtual = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const dataHora = new Date().toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  // B347 (AI_151) — campos do modelo oficial da RDC 1.000/2025 que precisam
  // aparecer mesmo sem dado no cadastro (linha em branco para preencher à mão).
  const LINHA_BRANCA = "______________________________";

  // Gerar conteúdo dos medicamentos para controle especial
  let medHtml = "";
  medicamentos.forEach((med, idx) => {
    const linhas = med.posologia.split("\n");
    const posologiaTexto = linhas[0] || "";
    const quantidadeTexto =
      linhas.length > 1
        ? linhas.slice(1).join(" ").replace("—", "").trim()
        : "";
    medHtml += `
      <div style="margin-bottom: 16px;">
        <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px;">
          <div style="font-weight: 700; font-size: 13px;">
            ${idx + 1}. ${escapeHtml(med.nome.split("\n")[0])}
          </div>
          ${
            quantidadeTexto
              ? `<div style="flex: 1; border-bottom: 1px solid #999; margin: 0 8px; min-width: 40px;"></div><div style="font-size: 12px; font-weight: 600; white-space: nowrap;">${escapeHtml(
                  quantidadeTexto
                )}</div>`
              : ""
          }
        </div>
        <div style="font-size: 12px; color: #444; padding-left: 20px;">${escapeHtml(
          posologiaTexto
        )}</div>
      </div>
    `;
  });

  // Função para gerar uma via do controle especial
  const gerarViaControle = (numVia: string) => `
  <div style="page-break-before: always;"></div>
  <!-- CABEÇALHO -->
  <div style="display: flex; align-items: center; gap: 16px; padding-bottom: 12px; border-bottom: 3px solid #2d6a2e; margin-bottom: 20px;">
    <div style="width: 80px; height: 80px; flex-shrink: 0;">
      <img src="${logoBase64}" alt="Logo" style="width: 100%; height: 100%; object-fit: contain;" />
    </div>
    <div style="flex: 1;">
      <h1 style="font-size: 18px; font-weight: 700; color: #2d6a2e; text-transform: uppercase; margin: 0 0 4px 0;">${escapeHtml(
        config.nomeAmbulatorio
      )}</h1>
      <p style="font-size: 13px; color: #444; font-style: italic; margin: 0;">${escapeHtml(
        config.nomeMedico
      )}${config.crm ? " — " + escapeHtml(config.crm) : ""}</p>
    </div>
  </div>

  <!-- BLOCO SUPERIOR: ID EMITENTE (com borda) + RECEITUÁRIO (sem borda) -->
  <!-- B347-FU (AI_151, achado de Leonardo na validação ao vivo): align-items
       flex-start. Sem isso o flex usa "stretch" e a caixa COM BORDA do emitente
       estica até a altura da coluna da direita — que ficou alta por causa do selo
       com QR (B340) —, deixando um vazio grande dentro da moldura. Agora a caixa
       acompanha o próprio conteúdo. -->
  <div style="display: flex; align-items: flex-start; margin-bottom: 16px;">
    <!-- COLUNA ESQUERDA: ID EMITENTE — com borda -->
    <div style="flex: 1; padding: 12px 16px; border: 1.5px solid #333;">
      <div style="font-weight: 700; font-size: 12px; text-align: center; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px;">Identificação do Emitente</div>
      <div style="font-size: 12px; line-height: 1.8;">
        <div><strong>Nome:</strong> ${escapeHtml(config.nomeMedico)}</div>
        <div><strong>CRM:</strong> ${escapeHtml(config.crm)}</div>
        <div><strong>Endereço:</strong> ${escapeHtml(config.endereco)}</div>
        <div><strong>Telefone:</strong> ${escapeHtml(config.telefone) || ""}</div>
        <div><strong>Cidade e UF:</strong> ${escapeHtml(config.cidade) || ""}</div>
      </div>
    </div>
    <!-- COLUNA DIREITA: RECEITUÁRIO CONTROLE ESPECIAL — sem borda -->
    <div style="flex: 1; padding: 12px 16px;">
      <div style="font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px;">Receituário Controle Especial</div>
      <div style="font-size: 12px; line-height: 1.8;">
        <div><strong>DATA:</strong> ${dataHora}</div>
        <!-- B347-FU2 (AI_151): as duas vias numa linha só — é como o MODELO
             OFICIAL as traz ("1ª via - Retenção pela Farmácia / 2ª via -
             Paciente"); eu é que as havia quebrado em duas.
             A linha "Data de impressão deste receituário" foi RETIRADA: no modelo
             da Anvisa ela é "(recomendável)" e existe porque o talão é impresso em
             bloco pela gráfica, com data diferente da prescrição. Aqui o documento
             nasce no ato de prescrever — impressão e emissão são o mesmo instante,
             e a data já aparece no campo DATA e no selo. Decisão de Leonardo. -->
        <div style="margin-top: 4px;">1ª via - Retenção pela Farmácia / 2ª via - Paciente</div>
      </div>
      <div style="margin-top: 24px; text-align: center;">
        ${
          seloCompacto
            ? `<div style="margin-bottom: 8px;">${seloCompacto}</div>`
            : ""
        }
        <div style="border-top: 1px solid #333; padding-top: 8px;">
          <div style="font-weight: 700; font-size: 12px;">ASSINATURA</div>
        </div>
      </div>
    </div>
  </div>

  <!-- DADOS DO PACIENTE — B347 (AI_151), RDC ANVISA nº 1.000/2025.
       O modelo oficial (Versão 2, rev. 13/03/2026) exige NOME COMPLETO,
       ENDEREÇO COMPLETO e CPF (ou passaporte, se estrangeiro) no bloco do
       paciente. Antes estes dois campos eram CONDICIONAIS e SUMIAM quando o
       cadastro não tinha o dado — a farmácia recebia o receituário sem o campo
       que a norma tornou obrigatório. Agora a linha sai sempre; faltando o
       dado, vai em BRANCO para preenchimento à mão. -->
  <div style="margin-bottom: 16px;">
    <!-- B347-FU3 (AI_151): o nome vinha em 13px sem negrito, menor e mais fraco
         que os rótulos "CPF:"/"Endereço:" logo abaixo — o dado mais importante do
         bloco era o menos destacado. -->
    <div style="font-size: 15px; font-weight: 700; margin-bottom: 4px;">${escapeHtml(
      paciente.nome
    )}</div>
    <div style="font-size: 12px;"><strong>CPF:</strong> ${
      escapeHtml(paciente.cpf) || LINHA_BRANCA
    }</div>
    <div style="font-size: 12px;"><strong>Endereço:</strong> ${
      escapeHtml(
        [
          paciente.logradouro,
          paciente.numero,
          paciente.complemento,
          paciente.bairro,
          paciente.cidade,
          paciente.estado,
        ]
          .filter(Boolean)
          .join(", ")
      ) || LINHA_BRANCA
    }</div>
  </div>

  <!-- MEDICAMENTOS -->
  <div style="min-height: 200px;">
    ${medHtml}
  </div>

  <!-- BLOCO INFERIOR: ID COMPRADOR + FORNECEDOR — dois quadrados separados -->
  <div style="display: flex; gap: 12px; margin-top: auto;">
    <!-- QUADRADO ESQUERDO: ID COMPRADOR -->
    <div style="flex: 1; padding: 12px 16px; border: 1.5px solid #333;">
      <div style="font-weight: 700; font-size: 11px; text-align: center; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Identificação do Comprador</div>
      <!-- B347: a RDC 1.000/2025 alterou o art. 55 da Portaria 344/98 — o
           comprador passou a ser identificado por CPF (ou passaporte), NÃO por
           RG. Ordem e rótulos seguem o modelo oficial Versão 2. -->
      <div style="font-size: 12px; line-height: 2.0;">
        <div><strong>Nome:</strong> ___________________________________</div>
        <div><strong>CPF:</strong> ___________________________________</div>
        <div><strong>Endereço:</strong> ___________________________________</div>
        <div><strong>Cidade e UF:</strong> ___________________________________</div>
        <div><strong>Telefone:</strong> ___________________________________</div>
      </div>
    </div>
    <!-- QUADRADO DIREITO: ID FORNECEDOR -->
    <div style="flex: 1; padding: 12px 16px; border: 1.5px solid #333;">
      <div style="font-weight: 700; font-size: 11px; text-align: center; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Identificação do Estabelecimento Dispensador</div>
      <div style="text-align: center; margin-top: 36px;">
          <div style="border-top: 1px solid #333; width: 80%; margin: 0 auto; padding-top: 6px; font-size: 11px;">DATA</div>
        </div>
        <div style="text-align: center; margin-top: 32px;">
          <div style="border-top: 1px solid #333; width: 80%; margin: 0 auto; padding-top: 6px; font-size: 11px;">ASSINATURA DO FARMACÊUTICO</div>
        </div>
    </div>
  </div>

  <!-- RODAPÉ -->
  <div style="margin-top: 16px; padding-top: 12px; border-top: 2px solid #2d6a2e; text-align: center;">
    <div style="font-size: 13px; font-weight: 600; color: #333;">${escapeHtml(
      config.nomeMedico
    )}${config.crm ? " — " + escapeHtml(config.crm) : ""}</div>
    ${
      config.endereco
        ? `<div style="font-size: 11px; color: #888;">${escapeHtml(
            config.endereco
          )}</div>`
        : ""
    }
    <div style="font-size: 13px; color: #333; text-align: right; margin-top: 8px;">${
      config.cidade ? escapeHtml(config.cidade) + ", " : ""
    }${dataAtual}</div>
  </div>
`;

  // Montar HTML final: extrair o body da receita simples e adicionar as 2 vias
  // Mais simples: usar montarHTMLMultiPagina com todo o conteúdo
  const paginaSimplesConteudo = `
    <div class="header">
      <div class="header-logo"><img src="${logoBase64}" alt="Logo" /></div>
      <div class="header-text">
        <h1>${escapeHtml(config.nomeAmbulatorio)}</h1>
        <p>${escapeHtml(config.nomeMedico)}${
    config.crm ? " — " + escapeHtml(config.crm) : ""
  }</p>
      </div>
    </div>
    <div class="paciente-info">
      <p><strong>Nome:</strong> ${escapeHtml(paciente.nome)}</p>
      ${
        paciente.cpf
          ? `<p><strong>CPF:</strong> ${escapeHtml(paciente.cpf)}</p>`
          : ""
      }
    </div>
    <div class="tipo-documento">Receituário</div>
    <div class="conteudo">${gerarConteudoReceituario(medicamentos)}</div>
    <div class="rodape">
      <div class="rodape-crm">${escapeHtml(config.nomeMedico)}${
    config.crm ? " — " + escapeHtml(config.crm) : ""
  }</div>
      ${
        config.endereco
          ? `<div class="rodape-endereco">${escapeHtml(config.endereco)}</div>`
          : ""
      }
      <div class="rodape-data" style="text-align: right; margin-top: 12px; font-size: 13px; color: #333;">${
        config.cidade ? escapeHtml(config.cidade) + ", " : ""
      }${dataAtual}</div>
      ${
        selo
          ? `<div style="margin-top: 14px; text-align: center;">${selo}</div>`
          : ""
      }
    </div>
  `;

  const estilosAnexos = (anexos || []).map((a) => a.estilos).join("\n");
  const corpoAnexos = (anexos || []).map((a) => a.corpo).join("");

  const conteudoCompleto =
    paginaSimplesConteudo + gerarViaControle("1") + gerarViaControle("2");

  const htmlFinal = montarHTMLMultiPagina(
    config,
    conteudoCompleto,
    `Receituário - ${paciente.nome}`,
    estilosAnexos,
    { corpoAnexos }
  );
  return htmlFinal;
}

export function gerarPDFReceituarioComControle(
  paciente: Paciente,
  medicamentos: { nome: string; posologia: string }[],
  config: ConfigPDF,
  anexos?: { estilos: string; corpo: string }[]
) {
  abrirPDFEmNovaJanela(
    montarReceituarioComControleHTML(paciente, medicamentos, config, anexos)
  );
}

// ============================================
// 💊 RECEITA DE ANTIMICROBIANO — RDC ANVISA nº 471/2021 (B306, AI_140)
// ============================================
// Documento DEDICADO (não confundir com o controle especial da 344/98):
// receita SIMPLES em 2 VIAS, válida por 10 dias, com retenção da 2ª via pela
// farmácia (Art. 5º). Campos obrigatórios da norma: paciente (nome, IDADE, SEXO),
// medicamento (DCB, dose/forma/posologia/quantidade), emitente (nome + CRM/UF,
// endereço, telefone, assinatura e carimbo) e data de emissão.
// B350 (AI_151) — o MESMO documento serve a dois regimes de retenção. A RDC
// 973/2025 sujeitou os análogos de GLP-1 ao modelo do antimicrobiano (2 vias,
// retenção de uma via), mudando só a VALIDADE. Parametrizado em vez de duplicado:
// um gerador só, dois regimes.
export type RegimeRetencao = {
  /** Título impresso no alto do documento. */
  titulo: string;
  /** Texto da validade na nota legal (ex.: "10 (dez)"). */
  validade: string;
};
/** RDC ANVISA nº 471/2021 — antimicrobianos (B306). */
export const REGIME_ANTIMICROBIANO: RegimeRetencao = {
  titulo: "Receita de Antimicrobiano",
  validade: "10 (dez)",
};
/** RDC ANVISA nº 973/2025 + IN nº 360/2025 Art. 2º — análogos de GLP-1 (B350). */
export const REGIME_GLP1: RegimeRetencao = {
  titulo: "Receita com Retenção — Análogo de GLP-1",
  validade: "90 (noventa)",
};

export function montarReceitaAntimicrobianoHTML(
  paciente: Paciente,
  medicamentos: { nome: string; posologia: string }[],
  config: ConfigPDF,
  // Anexo opcional impresso no MESMO documento, após as vias.
  anexos?: { estilos: string; corpo: string }[],
  // Selo COMPLETO ICP-Brasil + QR de validação (B290); renderizado na 1ª via
  // (paciente) só no PDF assinado.
  selo?: string,
  // Selo COMPACTO ICP-Brasil no campo ASSINATURA das 2 vias (só no assinado, B289).
  seloCompacto?: string,
  // B350 — qual regime de retenção reger este documento (validade e título).
  regime: RegimeRetencao = REGIME_ANTIMICROBIANO,
  // B356 (AI_155) — RET com N.º do SNCR: este layout vira só a VIA DO PACIENTE
  // (1 página); a folha oficial com o N.º entra nos anexos, logo depois.
  viaPacienteEletronica = false
): string {
  const dataAtual = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const dataHora = new Date().toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const idade = paciente.dataNascimento
    ? `${calcularIdade(paciente.dataNascimento)} anos`
    : "";
  const sexo =
    paciente.sexo === "M"
      ? "Masculino"
      : paciente.sexo === "F"
      ? "Feminino"
      : "";

  // Medicamentos (numerados, com posologia + quantidade) — mesmo estilo das vias.
  let medHtml = "";
  medicamentos.forEach((med, idx) => {
    const linhas = med.posologia.split("\n");
    const posologiaTexto = linhas[0] || "";
    const quantidadeTexto =
      linhas.length > 1 ? linhas.slice(1).join(" ").replace("—", "").trim() : "";
    medHtml += `
      <div style="margin-bottom: 16px;">
        <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px;">
          <div style="font-weight: 700; font-size: 13px;">${idx + 1}. ${escapeHtml(
      med.nome.split("\n")[0]
    )}</div>
          ${
            quantidadeTexto
              ? `<div style="flex: 1; border-bottom: 1px solid #999; margin: 0 8px; min-width: 40px;"></div><div style="font-size: 12px; font-weight: 600; white-space: nowrap;">${escapeHtml(
                  quantidadeTexto
                )}</div>`
              : ""
          }
        </div>
        <div style="font-size: 12px; color: #444; padding-left: 20px;">${escapeHtml(
          posologiaTexto
        )}</div>
      </div>
    `;
  });

  // Uma via (1ª = paciente; 2ª = retida na farmácia).
  const gerarVia = (numVia: "1" | "2") => `
  ${numVia === "1" ? "" : '<div style="page-break-before: always;"></div>'}
  <!-- CABEÇALHO -->
  <div style="display: flex; align-items: center; gap: 16px; padding-bottom: 12px; border-bottom: 3px solid #2d6a2e; margin-bottom: 16px;">
    <div style="width: 72px; height: 72px; flex-shrink: 0;">
      <img src="${logoBase64}" alt="Logo" style="width: 100%; height: 100%; object-fit: contain;" />
    </div>
    <div style="flex: 1;">
      <h1 style="font-size: 18px; font-weight: 700; color: #2d6a2e; text-transform: uppercase; margin: 0 0 4px 0;">${escapeHtml(
        config.nomeAmbulatorio
      )}</h1>
      <p style="font-size: 13px; color: #444; font-style: italic; margin: 0;">${escapeHtml(
        config.nomeMedico
      )}${config.crm ? " — " + escapeHtml(config.crm) : ""}</p>
    </div>
  </div>

  <!-- TÍTULO + VIA -->
  <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 14px;">
    <div style="font-weight: 700; font-size: 15px; text-transform: uppercase; letter-spacing: 0.5px; color: #2d6a2e;">${escapeHtml(regime.titulo)}</div>
    <div style="font-size: 12px; font-weight: 600; color: #333;">${
      viaPacienteEletronica
        ? "Via do paciente"
        : numVia === "1"
        ? "1ª via — Paciente"
        : "2ª via — Retida na farmácia"
    }</div>
  </div>

  <!-- BLOCO SUPERIOR: EMITENTE + DATA/ASSINATURA -->
  <!-- B347-FU — mesma correção do controle especial (ver comentário lá). -->
  <div style="display: flex; align-items: flex-start; margin-bottom: 16px;">
    <div style="flex: 1; padding: 12px 16px; border: 1.5px solid #333;">
      <div style="font-weight: 700; font-size: 12px; text-align: center; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px;">Identificação do Emitente</div>
      <div style="font-size: 12px; line-height: 1.8;">
        <div><strong>Nome:</strong> ${escapeHtml(config.nomeMedico)}</div>
        <div><strong>CRM:</strong> ${escapeHtml(config.crm)}</div>
        <div><strong>Endereço:</strong> ${escapeHtml(config.endereco)}</div>
        <div><strong>Telefone:</strong> ${escapeHtml(config.telefone) || ""}</div>
        <div><strong>Cidade e UF:</strong> ${escapeHtml(config.cidade) || ""}</div>
      </div>
    </div>
    <div style="flex: 1; padding: 12px 16px;">
      <div style="font-size: 12px; line-height: 1.8;">
        <div><strong>DATA:</strong> ${dataHora}</div>
      </div>
      <div style="margin-top: 24px; text-align: center;">
        ${seloCompacto ? `<div style="margin-bottom: 8px;">${seloCompacto}</div>` : ""}
        <div style="border-top: 1px solid #333; padding-top: 8px;">
          <div style="font-weight: 700; font-size: 12px;">ASSINATURA E CARIMBO</div>
        </div>
      </div>
    </div>
  </div>

  <!-- DADOS DO PACIENTE (nome, idade, sexo — exigidos pela RDC 471) -->
  <div style="margin-bottom: 16px; padding: 10px 16px; border: 1px solid #ccc; border-radius: 4px;">
    <div style="font-size: 13px; margin-bottom: 3px;"><strong>Paciente:</strong> ${escapeHtml(
      paciente.nome
    )}</div>
    <div style="font-size: 12px;">
      ${idade ? `<strong>Idade:</strong> ${idade}` : ""}${idade && sexo ? " &nbsp;·&nbsp; " : ""}${sexo ? `<strong>Sexo:</strong> ${sexo}` : ""}${paciente.cpf ? `${idade || sexo ? " &nbsp;·&nbsp; " : ""}<strong>CPF:</strong> ${escapeHtml(paciente.cpf)}` : ""}
    </div>
  </div>

  <!-- MEDICAMENTOS -->
  <div style="min-height: 180px;">
    ${medHtml}
  </div>

  <!-- NOTA LEGAL: validade + retenção (RDC 471/2021) -->
  <div style="margin-top: 12px; padding: 8px 12px; border-top: 1px solid #999; font-size: 10.5px; color: #555;">
    Receita válida por <strong>${escapeHtml(regime.validade)} dias</strong> a contar da data de emissão.${
      viaPacienteEletronica
        ? ""
        : ` A dispensação é feita com <strong>retenção da 2ª via</strong> pela farmácia; a 1ª via é devolvida ao paciente.`
    }
  </div>

  <!-- SELO + QR de validação (B290) nas DUAS vias quando assinado. B306-FU
       (AI_143, pedido de Leonardo na validação ao vivo): a 2ª via é RETIDA na
       farmácia — o farmacêutico é quem mais precisa validar a autenticidade
       pelo QR; antes o selo completo saía só na 1ª (via do paciente). -->
  ${
    selo
      ? `<div style="margin-top: 12px; text-align: center;">${selo}</div>`
      : ""
  }

  <!-- RODAPÉ -->
  <div style="margin-top: 10px; padding-top: 10px; border-top: 2px solid #2d6a2e; text-align: center;">
    <div style="font-size: 13px; font-weight: 600; color: #333;">${escapeHtml(
      config.nomeMedico
    )}${config.crm ? " — " + escapeHtml(config.crm) : ""}</div>
    ${config.endereco ? `<div style="font-size: 11px; color: #888;">${escapeHtml(config.endereco)}</div>` : ""}
    <div style="font-size: 13px; color: #333; text-align: right; margin-top: 6px;">${config.cidade ? escapeHtml(config.cidade) + ", " : ""}${dataAtual}</div>
  </div>
`;

  const estilosAnexos = (anexos || []).map((a) => a.estilos).join("\n");
  const corpoAnexos = (anexos || []).map((a) => a.corpo).join("");

  const conteudoCompleto = viaPacienteEletronica ? gerarVia("1") : gerarVia("1") + gerarVia("2");

  return montarHTMLMultiPagina(
    config,
    conteudoCompleto,
    `Receita de Antimicrobiano - ${paciente.nome}`,
    estilosAnexos,
    { corpoAnexos }
  );
}

export function gerarPDFReceitaAntimicrobiano(
  paciente: Paciente,
  medicamentos: { nome: string; posologia: string }[],
  config: ConfigPDF,
  anexos?: { estilos: string; corpo: string }[],
  // B350 — o mesmo documento serve ao regime de GLP-1 (validade de 90 dias).
  regime: RegimeRetencao = REGIME_ANTIMICROBIANO
) {
  abrirPDFEmNovaJanela(
    montarReceitaAntimicrobianoHTML(
      paciente,
      medicamentos,
      config,
      anexos,
      undefined,
      undefined,
      regime
    )
  );
}

// ============================================
// CONTEÚDO: LISTA DE EXAMES
// ============================================
// B298 (AI_136): cada exame é uma LINHA de tabela (<tr>) própria — não mais um
// bloco dentro de uma única célula gigante. Isso faz o table-footer-group
// reservar o espaço do carimbo (alto: selo+QR) corretamente em cada página, e o
// carimbo nunca fica órfão numa página sem exames (regra do Leonardo). `.exlin`
// tem break-inside:avoid para um exame não partir entre páginas.
function gerarConteudoExames(exames: ExamePDF[], observacao?: string): string {
  let html = "";
  exames.forEach((exame) => {
    html += `
      <tr><td class="exlin">
        <div style="display: flex; justify-content: space-between; align-items: baseline; padding: 6px 0; border-bottom: 1px dotted #ccc;">
          <span style="font-weight: 700; font-size: 13px;">${escapeHtml(
            exame.nome
          )}</span>
          <span style="font-size: 12px; color: #666;">TUSS: ${escapeHtml(
            exame.tuss
          )}</span>
        </div>
      </td></tr>
    `;
  });
  if (observacao) {
    html += `
      <tr><td class="exlin">
        <div style="margin-top: 16px; padding: 12px; background: #f9fafb; border-radius: 8px; font-size: 12px; color: #555; font-style: italic;">
          ${escapeHtml(observacao)}
        </div>
      </td></tr>
    `;
  }
  return html;
}

// ============================================
// CONTEÚDO: RECEITUÁRIO
// ============================================
function gerarConteudoReceituario(
  medicamentos: { nome: string; posologia: string; quantidade?: string }[]
): string {
  let html = "";
  medicamentos.forEach((med, idx) => {
    // Separar posologia do sufixo de uso/quantidade
    const linhas = med.posologia.split("\n");
    const posologiaTexto = linhas[0] || "";
    const quantidadeTexto =
      linhas.length > 1
        ? linhas.slice(1).join(" ").replace("—", "").trim()
        : "";

    html += `
      <div style="margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px;">
          <div style="font-weight: 700; font-size: 14px;">
            ${idx + 1}. ${escapeHtml(med.nome.split("\n")[0])}${
      quantidadeTexto ? " " : ""
    }
          </div>
          ${
            quantidadeTexto
              ? `<div style="flex: 1; border-bottom: 1px solid #999; margin: 0 8px; min-width: 40px;"></div><div style="font-size: 13px; font-weight: 600; white-space: nowrap;">${escapeHtml(
                  quantidadeTexto
                )}</div>`
              : ""
          }
        </div>
        <div style="font-size: 13px; color: #444; padding-left: 24px;">${escapeHtml(
          posologiaTexto
        )}</div>
      </div>
    `;
  });
  return html;
}

// ============================================
// MONTAR HTML COM MÚLTIPLAS PÁGINAS
// ============================================
function montarHTMLMultiPagina(
  config: ConfigPDF,
  conteudoPaginas: string,
  titulo?: string,
  estilosExtras?: string,
  // B289 (AI_128) — corpoAnexos: fichas SUS, renderizadas FORA das tabelas das
  // seções (mantêm o layout oficial, sem o cabeçalho/rodapé do app).
  opcoes?: { corpoAnexos?: string }
): string {
  return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>${escapeHtml(titulo) || "Documento"}</title>
  <style>
    @page {
      size: A4;
      margin: 20mm 15mm 25mm 15mm;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
      color: #1a1a1a;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .header {
      display: flex;
      align-items: center;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 3px solid #2d6a2e;
      margin-bottom: 24px;
    }
    .header-logo {
      width: 80px;
      height: 80px;
      flex-shrink: 0;
    }
    .header-logo img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    .header-text {
      flex: 1;
    }
    .header-text h1 {
      font-size: 18px;
      font-weight: 700;
      color: #2d6a2e;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    .header-text p {
      font-size: 13px;
      color: #444;
      font-style: italic;
    }
    .paciente-info {
      margin-bottom: 16px;
    }
    .paciente-info p {
      font-size: 14px;
      margin-bottom: 4px;
    }
    .paciente-info strong {
      font-weight: 700;
    }
    .tipo-documento {
      font-size: 15px;
      font-weight: 700;
      font-style: italic;
      margin-bottom: 12px;
    }
    .conteudo {
      min-height: auto;
    }
    .rodape {
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid #2d6a2e;
      text-align: center;
    }
    .rodape-crm {
      font-size: 13px;
      font-weight: 600;
      color: #333;
      margin-bottom: 4px;
    }
    .rodape-endereco {
      font-size: 11px;
      color: #888;
    }
    .rodape-data {
      font-size: 11px;
      color: #888;
      margin-top: 8px;
    }
    /* B289 (AI_128): cada seção do pedido é uma tabela própria — thead repete o
       cabeçalho + identificação do paciente em TODA página; o selo GRANDE fica no
       fim (última página) sem partir. Anexos SUS ficam FORA das tabelas. */
    .doc-layout { width: 100%; border-collapse: collapse; }
    .doc-layout > thead { display: table-header-group; }
    .doc-layout > tfoot { display: table-footer-group; }
    .doc-layout > thead > tr > td,
    .doc-layout > tfoot > tr > td,
    .doc-layout > tbody > tr > td { padding: 0; border: none; vertical-align: top; }
    /* B298 (AI_136): cada exame numa linha própria; não parte entre páginas.
       É o que faz o tfoot (carimbo alto) reservar espaço e nunca ficar órfão. */
    .doc-layout > tbody > tr > td.exlin { break-inside: avoid; }
    /* Cada seção nova (exame de imagem) começa em página nova. */
    .doc-layout > tbody > tr > td.sec-nova { break-before: page; }
    .paciente-cab { margin-bottom: 16px; }
    .paciente-cab p { font-size: 14px; margin-bottom: 4px; }
    .paciente-cab strong { font-weight: 700; }
    .rodape { break-inside: avoid; }
    @media print {
      body { margin: 0; }
      .no-print { display: none !important; }
    }
    ${estilosExtras || ""}
  </style>
</head>
<body>
  <div class="no-print" style="text-align: center; padding: 12px; background: #f0f0f0;">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>
  ${conteudoPaginas}${opcoes?.corpoAnexos || ""}
</body>
</html>
  `;
}

// ============================================
// MONTAR HTML COMPLETO DO DOCUMENTO
// ============================================
function montarHTML(
  config: ConfigPDF,
  paciente: Paciente,
  tipoDocumento: string,
  conteudo: string,
  // Anexos (ex.: LME) impressos após o documento, cada um em folha própria.
  anexos?: { estilos: string; corpo: string }[],
  // B250 (AI_113) — rodapé só com Cidade, Data (sem nome/CRM/endereço).
  // B212 F4 (AI_126) — seloAssinatura: HTML do selo ICP-Brasil (só no PDF assinado).
  opcoes?: { rodapeSimplificado?: boolean; seloAssinatura?: string }
): string {
  const dataAtual = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const estilosAnexos = (anexos || []).map((a) => a.estilos).join("\n");
  const corpoAnexos = (anexos || []).map((a) => a.corpo).join("");

  return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>${escapeHtml(tipoDocumento)} - ${escapeHtml(paciente.nome)}</title>
  <style>
    @page {
      size: A4;
      margin: 20mm 15mm 25mm 15mm;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
      color: #1a1a1a;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page {
      max-width: 210mm;
      margin: 0 auto;
      padding: 0;
    }
    .header {
      display: flex;
      align-items: center;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 3px solid #2d6a2e;
      margin-bottom: 24px;
    }
    .header-logo {
      width: 80px;
      height: 80px;
      flex-shrink: 0;
    }
    .header-logo img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    .header-text {
      flex: 1;
    }
    .header-text h1 {
      font-size: 18px;
      font-weight: 700;
      color: #2d6a2e;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    .header-text p {
      font-size: 13px;
      color: #444;
      font-style: italic;
    }
    .paciente-info {
      margin-bottom: 16px;
    }
    .paciente-info p {
      font-size: 14px;
      margin-bottom: 4px;
    }
    .paciente-info strong {
      font-weight: 700;
    }
    .tipo-documento {
      font-size: 15px;
      font-weight: 700;
      font-style: italic;
      margin-bottom: 12px;
    }
    .conteudo {
      min-height: auto;
    }
    .rodape {
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid #2d6a2e;
      text-align: center;
    }
    .rodape-crm {
      font-size: 13px;
      font-weight: 600;
      color: #333;
      margin-bottom: 4px;
    }
    .rodape-endereco {
      font-size: 11px;
      color: #888;
    }
    .rodape-data {
      font-size: 11px;
      color: #888;
      margin-top: 8px;
    }
    /* B289 (AI_128): cabeçalho e linha de identificação/assinatura repetidos em
       TODA página. thead/tfoot de tabela repetem a cada quebra de página na
       impressão e no PDF — e ficam confinados ao documento (os anexos SUS, fora
       da tabela, mantêm seu layout oficial). */
    .doc-layout { width: 100%; border-collapse: collapse; }
    .doc-layout > thead { display: table-header-group; }
    .doc-layout > tfoot { display: table-footer-group; }
    .doc-layout > tbody > tr > td,
    .doc-layout > thead > tr > td,
    .doc-layout > tfoot > tr > td { padding: 0; border: none; vertical-align: top; }
    /* B289 (AI_128): identificação do paciente no cabeçalho repetível (toda
       página). O selo GRANDE vai no tfoot — repete no rodapé de TODA página
       (nunca órfão), decisão de Leonardo. Nome/CPF grandes e em linhas separadas
       (como era antes — pedido de Leonardo). */
    .paciente-cab { margin-bottom: 16px; }
    .paciente-cab p { font-size: 14px; margin-bottom: 4px; }
    .paciente-cab strong { font-weight: 700; }
    /* o bloco do rodapé (nome/CRM/data + selo) não pode partir entre páginas. */
    .rodape { break-inside: avoid; }
    @media print {
      body { margin: 0; }
      .no-print { display: none !important; }
    }
    ${estilosAnexos}
  </style>
</head>
<body>
  <!-- BOTÃO IMPRIMIR (não aparece na impressão) -->
  <div class="no-print" style="text-align: center; padding: 12px; background: #f0f0f0;">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>

  <div class="page">
   <table class="doc-layout">
    <!-- CABEÇALHO + identificação do paciente repetidos em TODA página (thead) -->
    <thead><tr><td>
      <div class="header">
        <div class="header-logo">
          <img src="${logoBase64}" alt="Logo" />
        </div>
        <div class="header-text">
          <h1>${escapeHtml(config.nomeAmbulatorio)}</h1>
          <p>${escapeHtml(config.nomeMedico)}${
    config.crm ? ` — ${escapeHtml(config.crm)}` : ""
  }</p>
        </div>
      </div>
      <div class="paciente-cab">
        <p><strong>Nome:</strong> ${escapeHtml(paciente.nome)}</p>
        ${
          paciente.cpf
            ? `<p><strong>CPF:</strong> ${escapeHtml(paciente.cpf)}</p>`
            : ""
        }
      </div>
    </td></tr></thead>

    <!-- RODAPÉ (selo completo) repetido no rodapé de TODA página (tfoot) — nunca
         fica órfão e não parte. Decisão de Leonardo (AI_128). -->
    <tfoot><tr><td>
    ${
      opcoes?.seloAssinatura
        ? // B298 (AI_136) — ASSINADO: rodapé enxuto (data + selo). Nome/CRM/endereço
          // já vão no CABEÇALHO (thead) e DENTRO do selo → repeti-los aqui deixava o
          // carimbo alto demais para o Chromium repetir o tfoot em toda página (ele
          // sumia das páginas do meio e transbordava órfão). Enxuto → repete e reserva.
          `<div class="rodape">
      <div class="rodape-data" style="text-align: right; font-size: 13px; color: #333;">${
        config.cidade ? escapeHtml(config.cidade) + ", " : ""
      }${dataAtual}</div>
      <div style="margin-top: 14px; text-align: center;">${opcoes.seloAssinatura}</div>
    </div>`
        : opcoes?.rodapeSimplificado
        ? `<div class="rodape">
      <div class="rodape-data" style="text-align: right; font-size: 13px; color: #333;">${
        config.cidade ? escapeHtml(config.cidade) + ", " : ""
      }${dataAtual}</div>
    </div>`
        : `<div class="rodape">
      <div class="rodape-crm">${escapeHtml(config.nomeMedico)}${
            config.crm ? ` — ${escapeHtml(config.crm)}` : ""
          }</div>
      ${
        config.endereco
          ? `<div class="rodape-endereco">${escapeHtml(config.endereco)}</div>`
          : ""
      }
      <div class="rodape-data" style="text-align: right; margin-top: 12px; font-size: 13px; color: #333;">${
        config.cidade ? escapeHtml(config.cidade) + ", " : ""
      }${dataAtual}</div>
    </div>`
    }
    </td></tr></tfoot>

    <tbody><tr><td>
    <!-- TIPO DO DOCUMENTO -->
    <div class="tipo-documento">${escapeHtml(tipoDocumento)}</div>

    <!-- CONTEÚDO -->
    <div class="conteudo">
      ${conteudo}
    </div>
    </td></tr></tbody>
   </table>
  </div>
  ${corpoAnexos}
</body>
</html>
  `;
}

// ============================================
// ABRIR PDF EM NOVA JANELA
// ============================================
export function abrirPDFEmNovaJanela(html: string) {
  // Detectar Safari
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  if (isSafari) {
    // Safari: usar iframe oculto para evitar bugs de document.write + print
    // Remove iframe anterior se existir
    const iframeAntigo = document.getElementById("hepato-print-frame");
    if (iframeAntigo) iframeAntigo.remove();

    const iframe = document.createElement("iframe");
    iframe.id = "hepato-print-frame";
    iframe.style.position = "fixed";
    iframe.style.top = "0";
    iframe.style.left = "0";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.zIndex = "99999";
    iframe.style.background = "white";
    iframe.style.border = "none";

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      alert("Erro ao gerar documento.");
      iframe.remove();
      return;
    }

    // Adicionar botão de fechar no HTML (substitui o botão de fechar original)
    const htmlComFechar = html.replace(
      `<button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>`,
      `<button onclick="parent.document.getElementById('hepato-print-frame').remove()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>`
    );

    // Substituir window.print() por chamada via iframe
    const htmlComPrint = htmlComFechar.replace(
      `onclick="window.print()"`,
      `onclick="window.print()"`
    );

    iframeDoc.open();
    iframeDoc.write(htmlComPrint);
    iframeDoc.close();
  } else {
    // Chrome/Firefox: comportamento original (funciona bem)
    const novaJanela = window.open("about:blank", "_blank");
    if (!novaJanela) {
      alert(
        "Não foi possível abrir a janela. Verifique se o bloqueador de pop-ups está ativo."
      );
      return;
    }
    novaJanela.document.open();
    novaJanela.document.write(html);
    novaJanela.document.close();
    setTimeout(() => novaJanela.focus(), 300);
  }
}
